<template>
  <div class="mt-3">
    <template v-if="erpCamposIntegracao && showCamposAdicionais">
      <px-campos-integracao type="compra-item"
                            dense
                            :single-line="false"
                            :readonly="isCadastrosAdicionaisReadonly"
                            :form-data="produto.cadastrosAdicionais"
                            @replicar-campo="onReplicarCampo"/>
    </template>
    <v-row dense>
      <v-col cols="12" sm="6" v-if="produto.especificacaoFornecedor">
        <span class="caption font-weight-bold">Especificação do fornecedor</span>
        <div>
          <px-show-large-text :large-text="produto.especificacaoFornecedor"/>
        </div>
      </v-col>
      <v-col cols="12" sm="6" v-if="produto.observacaoInterna">
        <span class="caption font-weight-bold">Observações internas</span>
        <div>
          <px-show-large-text :large-text="produto.observacaoInterna"/>
        </div>
      </v-col>
    </v-row>
    <v-row dense class="mt-3">
      <v-col cols="12">
        <px-autocomplete-nota-entrada
          dense
          @click.stop.prevent="() => {}"
          :class="`rounded-tag-small ${isTipoNotaEntradaRequired ? 'required' : ''}`"
          :error="$v.produto.tipoNotaEntrada.$error"
          @blur="$v.produto.tipoNotaEntrada.$touch()"
          v-model="produto.tipoNotaEntrada"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" sm="6">
        <px-select-enum
          dense
          label="Finalidade da compra"
          :items="finalidades"
          class="rounded-tag-small"
          v-model="produto.finalidadeCompra"/>
      </v-col>
      <v-col cols="12" sm="3">
        <px-date-picker dense
                        label="Prazo de entrega solicitado"
                        input-class="required rounded-tag-small"
                        :error="$v.produto.dataPrevisaoEntrega.$error"
                        @blur="$v.produto.dataPrevisaoEntrega.$touch()"
                        v-model="produto.dataPrevisaoEntrega"/>
      </v-col>
      <v-col cols="12" sm="3">
        <px-date-picker dense
                        label="Data de faturamento"
                        input-class="required rounded-tag-small"
                        :error="$v.produto.dataFaturamento.$error"
                        @blur="$v.produto.dataFaturamento.$touch()"
                        v-model="produto.dataFaturamento"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" md="6">
        <px-autocomplete-projeto
          dense
          :class="`rounded-tag-small ${isProjetoRequired ? 'required' : ''}`"
          :disabled="isProjetoReadonly"
          :error="$v.produto.projeto.$error"
          @blur="$v.produto.projeto.$touch()"
          v-model="produto.projeto"/>
      </v-col>
      <v-col cols="12" md="6">
        <px-autocomplete-centro-custo
          dense
          :class="`rounded-tag-small ${isCentroCustoRequired ? 'required' : ''}`"
          :disabled="isCentroCustoReadonly"
          :error="$v.produto.centroCusto.$error"
          @blur="$v.produto.centroCusto.$touch()"
          v-model="produto.centroCusto"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" md="6">
        <v-textarea
          outlined
          auto-grow
          label="Especificação"
          counter="1500"
          rows="3"
          class="rounded-tag-small"
          v-model="produto.especificacaoProduto"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-textarea
          outlined
          auto-grow
          label="Observações externas"
          counter="1500"
          rows="3"
          class="rounded-tag-small"
          v-model="produto.observacaoExterna"
        />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" sm="4" v-if="hasTaxaFinanceira">
        <px-input-currency
          dense
          label="Preço unitário com taxa"
          class="rounded-tag-small"
          ref="precoUnitarioTaxa"
          :value="precoUnitarioTaxa"
          :precision="precision"
          disabled/>
      </v-col>
      <v-col cols="12" :sm="smHasTaxaFinanceira">
        <px-input-currency
          dense
          disabled
          label="Desconto"
          class="rounded-tag-small"
          :precision="precision"
          v-model="savingConvertido"/>
      </v-col>
      <v-col cols="12" :sm="smHasTaxaFinanceira">
        <px-input-currency
          dense
          disabled
          label="Valor total DIFAL"
          class="rounded-tag-small"
          v-model="valorDifalConvertido"/>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6"
             v-if="produto.arquivosComprador && produto.arquivosComprador.length">
        <span class="caption font-weight-bold">Arquivos cotados</span>
        <px-anexo-chip :files="produto.arquivosComprador"
                       download-base-path="files/download-comprador"/>
      </v-col>
      <v-col cols="12" sm="6"
             v-if="produto.arquivosFornecedor && produto.arquivosFornecedor.length">
        <span class="caption font-weight-bold">Arquivos do fornecedor</span>
        <px-anexo-chip :files="produto.arquivosFornecedor"
                       download-base-path="files/download-fornecedor"/>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { requiredIf } from 'vuelidate/lib/validators';
import {
  isFieldRequiredEntityItens,
  isFieldReadonlyEntityItens,
} from '@/modules/erp-campos/erp-campos-utils';
import validationMixin from '@/mixins/validation-mixin';
import { FinalidadeCompra, ErpCamposIntegracao, TipoProduto } from 'px-business-components';
import { isDateAfterOrSameToday } from '@/utils/date-utils';
import { calculateTaxa } from '@/utils/taxa-utils';
import { getPrecision } from '@/utils/produto-utils';

export default {
  mixins: [validationMixin],
  props: {
    finalizacao: {
      type: Object,
      required: false,
    },
    produto: {
      type: Object,
      required: true,
    },
    erpCamposIntegracao: {
      type: String,
    },
    camposObrigatorios: Object,
    camposReadonly: Object,
    hasAnyItemFromRequisicao: Boolean,
  },
  data() {
    return {
      finalidades: FinalidadeCompra.values,
    };
  },
  computed: {
    ...mapGetters('entidade', ['hasTaxaFinanceira']),
    isCentroCustoRequired() {
      return isFieldRequiredEntityItens('centroCusto', this.camposObrigatorios);
    },
    isProjetoRequired() {
      return isFieldRequiredEntityItens('projeto', this.camposObrigatorios);
    },
    precoUnitarioTaxa() {
      if (!this.finalizacao
        || !this.produto.precoUnitario
        || !this.finalizacao.percentualTaxa
        || !this.finalizacao.prazoPagamento) {
        return 0;
      }
      return calculateTaxa(
        this.finalizacao.prazoPagamento,
        this.produto.precoUnitario,
        this.finalizacao.percentualTaxa,
      );
    },
    savingConvertido() {
      return this.produto.valorMoedaConvertido.saving
        * this.produto.quantidade / this.produto.$$quantidadeOriginal;
    },
    valorDifalConvertido() {
      return this.produto.impostos.valorDifalConvertido * this.produto.quantidade;
    },
    isCentroCustoReadonly() {
      return isFieldReadonlyEntityItens('centroCusto', this.camposReadonly) && this.hasAnyItemFromRequisicao;
    },
    isProjetoReadonly() {
      return isFieldReadonlyEntityItens('projeto', this.camposReadonly) && this.hasAnyItemFromRequisicao;
    },
    isCadastrosAdicionaisReadonly() {
      return isFieldReadonlyEntityItens('cadastrosAdicionais', this.camposReadonly) && this.hasAnyItemFromRequisicao;
    },
    smHasTaxaFinanceira() {
      return this.hasTaxaFinanceira ? 4 : 6;
    },
    precision() {
      return getPrecision(this.produto.produto);
    },
    showCamposAdicionais() {
      if (this.erpCamposIntegracao === ErpCamposIntegracao.keys.SENIOR_PFIFFNER.key) {
        return this.produto.produto.tipoProduto === TipoProduto.keys.PRODUTO.key;
      }
      return this.erpCamposIntegracao !== null;
    },
    isTipoNotaEntradaRequired() {
      return isFieldRequiredEntityItens('tipoNotaEntrada', this.camposObrigatorios);
    },
  },
  methods: {
    validate() {
      const isInvalid = this.validateForm();
      if (isInvalid) {
        this.$emit('invalid');
      }
      return isInvalid;
    },
    onReplicarCampo(campo) {
      this.$emit('replicar-campo', campo);
    },
  },
  validations: {
    produto: {
      centroCusto: {
        required: requiredIf(function () {
          return this.isCentroCustoRequired;
        }),
      },
      projeto: {
        required: requiredIf(function () {
          return this.isProjetoRequired;
        }),
      },
      dataFaturamento: {
        isDateAfterOrSameToday,
      },
      dataPrevisaoEntrega: {
        isDateAfterOrSameToday,
      },
      tipoNotaEntrada: {
        required: requiredIf(function () {
          return this.isTipoNotaEntradaRequired;
        }),
      },
    },
  },
};
</script>
